import React, { useMemo } from 'react'
import styled from 'styled-components'
import { ink, line, font } from 'features/dashboard/tokens'

const niceCeil = value => {
  if (value <= 0) return 1
  const mag = Math.pow(10, Math.floor(Math.log10(value)))
  return Math.ceil(value / mag) * mag
}

const axisLabel = (m, index) => {
  const abbrev = String(m.label || '').split(' ')[0]
  const [year, mo] = String(m.month || '').split('-')
  const showYear = index === 0 || mo === '01'
  return showYear && year ? `${abbrev} ${year.slice(2)}` : abbrev
}

const MonthlyStackedBars = ({ months = [], buckets = [], height = 220, onBarClick }) => {
  const max = useMemo(
    () => niceCeil(Math.max(0, ...months.map(m => buckets.reduce((s, b) => s + Number(m[b.key] ?? 0), 0)))),
    [months, buckets],
  )

  if (!months.length) return <Empty>No tasks with a deadline in this range.</Empty>

  return (
    <Wrap>
      <Legend>
        {buckets.map(b => (
          <LegendItem key={b.key} title={b.help}>
            <Swatch style={{ background: b.tint }} />{b.label}
          </LegendItem>
        ))}
      </Legend>

      <Plot style={{ height }}>
        <YAxis>
          <YTick>{max.toLocaleString('en-GB')}</YTick>
          <YTick>{Math.round(max / 2).toLocaleString('en-GB')}</YTick>
          <YTick>0</YTick>
        </YAxis>
        <Bars>
          {months.map(m => {
            const total = buckets.reduce((s, b) => s + Number(m[b.key] ?? 0), 0)
            const clickable = !!onBarClick && total > 0
            return (
              <Column key={m.month}>
                <Stack
                  as={clickable ? 'button' : 'div'}
                  type={clickable ? 'button' : undefined}
                  $clickable={clickable}
                  onClick={clickable ? () => onBarClick(m) : undefined}
                  title={`${m.label}: ${total} task${total === 1 ? '' : 's'}`}
                  aria-label={`${m.label}, ${total} tasks`}
                >
                  {buckets.map(b => {
                    const v = Number(m[b.key] ?? 0)
                    if (!v) return null
                    return (
                      <Seg
                        key={b.key}
                        style={{ height: `${(v / max) * 100}%`, background: b.tint }}
                        title={`${m.label}: ${b.label}: ${v}`}
                      />
                    )
                  })}
                </Stack>
                <XLabel $now={m.isCurrent} title={m.label}>
                  <FullLabel>{m.label}</FullLabel>
                  <ShortLabel>{axisLabel(m, months.indexOf(m))}</ShortLabel>
                </XLabel>
              </Column>
            )
          })}
        </Bars>
      </Plot>
    </Wrap>
  )
}

export default MonthlyStackedBars

const Wrap = styled.div`width: 100%;`
const Legend = styled.div`display: flex; flex-wrap: wrap; gap: 6px 14px; margin-bottom: 12px;`
const LegendItem = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: ${font.caption}; color: ${ink.mute}; cursor: help; white-space: nowrap;
`
const Swatch = styled.span`width: 10px; height: 10px; border-radius: 3px;`
const Plot = styled.div`display: flex; gap: 10px; width: 100%;`
const YAxis = styled.div`
  display: flex; flex-direction: column; justify-content: space-between;
  width: 36px; flex-shrink: 0; padding-bottom: 22px; text-align: right;
`
const YTick = styled.span`font-size: 10.5px; color: ${ink.mute}; font-variant-numeric: tabular-nums; line-height: 1;`
const Bars = styled.div`
  flex: 1; display: flex; align-items: flex-end; gap: 8px; min-width: 0;
  border-left: 1px solid ${line.base}; border-bottom: 1px solid ${line.base}; padding: 0 4px;
  @media (max-width: 768px) { gap: 3px; padding: 0 2px; }
`
const Column = styled.div`flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; min-width: 0;`
const Stack = styled.div`
  width: 100%; max-width: 56px; flex: 1; display: flex; flex-direction: column-reverse;
  justify-content: flex-start; border: none; background: transparent; padding: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  border-radius: 3px 3px 0 0; overflow: hidden;
  &:hover { opacity: ${({ $clickable }) => ($clickable ? 0.85 : 1)}; }
`
const Seg = styled.div`width: 100%; min-height: 2px;`
const XLabel = styled.span`
  margin-top: 6px; font-size: 10.5px; white-space: nowrap; min-width: 0;
  color: ${({ $now }) => ($now ? ink.base : ink.mute)};
  font-weight: ${({ $now }) => ($now ? 700 : 500)};
`
const FullLabel = styled.span`
  @media (max-width: 768px) { display: none; }
`
const ShortLabel = styled.span`
  display: none;
  @media (max-width: 768px) { display: inline; font-size: 9.5px; }
`
const Empty = styled.div`font-size: ${font.small}; color: ${ink.mute}; padding: 20px 2px;`
